import { useEffect, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { StyleSheet, Text, View } from 'react-native';

import { GiftedPersonData, giftedPersonResolver } from '@/lib/validations/GiftShowSchema';
import { i18n } from '@/utils/i18n';

import BrandedButton from '../buttons/BrandedButton';
import PhoneInput from '../inputs/PhoneInput';
import TextInputField from '../inputs/TextInputField';

interface GiftedPersonFormProps {
  initialData?: GiftedPersonData;
  doneLabel?: string;
  showDoneButton?: boolean;
  phonesOnList?: string[];
  onDone: (data: GiftedPersonData) => void;
}

const GiftedPersonForm = ({
  initialData,
  doneLabel = i18n.t('giftShows.add_another'),
  showDoneButton = true,
  phonesOnList,
  onDone
}: GiftedPersonFormProps) => {
  const [shouldClearField, setShouldClearField] = useState(false);

  const {
    control,
    handleSubmit,
    setFocus,
    reset,
    resetField,
    trigger,
    setValue,
    setError,
    formState: { isValid, submitCount, errors }
  } = useForm<GiftedPersonData>({
    defaultValues: initialData ?? {},
    mode: 'onChange',
    resolver: giftedPersonResolver
  });

  const onSubmit: SubmitHandler<GiftedPersonData> = async (data) => {
    if (onDone) {
      setShouldClearField(true);
      reset();
      onDone(data);
    }
  };

  useEffect(() => {
    if (initialData) {
      reset(initialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData]);

  return (
    <View style={styles.container}>
      <View style={styles.phoneContainer}>
        <PhoneInput
          placeholder={i18n.t('mix.phone')}
          variant="small_border_radius"
          showError
          error={errors.phone}
          shouldClearField={shouldClearField}
          onNumberChange={(number) => {
            setShouldClearField(false);
            if (number) {
              const fullNumber = `+${number?.getCountryCode()}${number?.getNationalNumber()}`;
              if (!phonesOnList?.find((phoneListed) => phoneListed === fullNumber)) {
                setValue('phone', fullNumber);
                trigger('phone');
              } else {
                resetField('phone');
                setError('phone', {
                  type: 'required',
                  message: i18n.t('giftShows.phone_already_on_list')
                });
              }
            } else {
              resetField('phone');
              setError('phone', {
                type: 'required',
                message: i18n.t('mix.valid_phone_number')
              });
            }
          }}
        />
      </View>
      <View style={styles.namesContainer}>
        <View style={styles.halfFlex}>
          <TextInputField
            name="firstName"
            control={control}
            placeholder={i18n.t('giftShows.firstName_label')}
            variant="small_border_radius"
            returnKeyLabel="next"
            returnKeyType="next"
            onEndEditing={() => setFocus('lastName')}
          />
        </View>
        <View style={styles.halfFlex}>
          <TextInputField
            name="lastName"
            control={control}
            placeholder={i18n.t('giftShows.lastName_label')}
            variant="small_border_radius"
            returnKeyLabel="done"
            returnKeyType="done"
          />
        </View>
      </View>
      {errors && <Text style={styles.errorText}>{errors.root?.message}</Text>}
      {showDoneButton && (
        <BrandedButton
          disabled={!isValid && submitCount > 0}
          onPress={handleSubmit(onSubmit)}
          style={styles.addOneButton}>
          {doneLabel}
        </BrandedButton>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    width: '100%'
  },

  phoneContainer: {
    zIndex: 99
  },

  halfFlex: {
    flex: 1 / 2
  },

  namesContainer: {
    flexDirection: 'row',
    width: '100%',
    gap: 5,
    marginTop: 5,
    justifyContent: 'center',
    alignItems: 'flex-start'
  },

  addOneButton: {
    width: '100%',
    backgroundColor: '#FE9722',
    marginVertical: 10
  },

  errorText: {
    color: 'red',
    fontSize: 20,
    fontWeight: 'bold'
  }
});

export default GiftedPersonForm;
